import React, { useMemo, Component } from 'react';
import { Target, ArrowRight, Clock } from 'lucide-react';
import { useAppStore } from '../app/hooks/useAppStore';
import { getWeakestFamiliesGlobal, countFamiliesByMasteryLevel, getFamilyMasteryKey } from '../mastery';
import { getFamilyDefinition, MODULES, QUESTIONS_BY_FAMILY, getAllowedTiersForGroup } from '../content';
import { getLDG, calculateFrontier } from '../governance';
import { buildFrontierMasteryInputs, MODE_REGISTRY } from '../modes';
import type { ModeId as RegistryModeId } from '../modes';
import type { GroupId } from '../config/systemConfig';
import { CardSurface } from './shared/CardSurface';
import { MODE_ICON } from './shared/modeTheme';
// ─── Types ────────────────────────────────────────────────────
interface MissionBriefingProps {
  onModeClick: (modeId: string, familyId?: string) => void;
}
interface Recommendation {
  id: string;
  familyId?: string;
  title: string;
  reason: string;
  color: 'amber' | 'indigo' | 'violet' | 'teal';
}
// ─── Color Palette ────────────────────────────────────────────
const COLOR_CLASSES = {
  amber: {
    bg: 'bg-amber-100',
    text: 'text-amber-700',
    border: 'border-amber-300',
    gradient: 'from-amber-400 to-orange-500',
    shadow: 'shadow-amber-200'
  },
  indigo: {
    bg: 'bg-indigo-100',
    text: 'text-indigo-700',
    border: 'border-indigo-300',
    gradient: 'from-indigo-400 to-purple-500',
    shadow: 'shadow-indigo-200'
  },
  violet: {
    bg: 'bg-violet-100',
    text: 'text-violet-700',
    border: 'border-violet-300',
    gradient: 'from-violet-400 to-fuchsia-500',
    shadow: 'shadow-violet-200'
  },
  teal: {
    bg: 'bg-teal-100',
    text: 'text-teal-700',
    border: 'border-teal-300',
    gradient: 'from-teal-400 to-emerald-500',
    shadow: 'shadow-teal-200'
  }
} as const;
// ─── Helpers ──────────────────────────────────────────────────
/**
 * Check if a family has actual questions available for a given group.
 * Prevents recommending families that exist in taxonomy but have
 * no questions within the allowed tier range for the student's group.
 */
function hasFamilyQuestionsForGroup(familyId: string, group: GroupId): boolean {
  const familyQuestions = QUESTIONS_BY_FAMILY.get(familyId);
  if (!familyQuestions || familyQuestions.length === 0) return false;
  const allowedTiers = getAllowedTiersForGroup(group);
  return familyQuestions.some(q => allowedTiers.has(q.tier));
}
// ─── Recommendation Engine ────────────────────────────────────
function buildRecommendations(familyMastery: Record<string, {
  masteryScore: number;
  attempts: number;
}>, selectedGroup: GroupId): Recommendation[] {
  const recs: Recommendation[] = [];
  // 1. LDG Frontier (Volgende stap)
  const masteryInputs = buildFrontierMasteryInputs(familyMastery, selectedGroup);
  const frontierResult = calculateFrontier(getLDG(), masteryInputs, {
    maxFrontierSize: 5,
    masteryThreshold: 80
  });
  if (frontierResult.frontier.length > 0) {
    const playableFrontier = frontierResult.frontier.filter(f => hasFamilyQuestionsForGroup(f.familyId, selectedGroup));
    playableFrontier.slice(0, 2).forEach((f, idx) => {
      recs.push({
        id: 'sprint',
        familyId: f.familyId,
        title: f.skill,
        reason: `Volgende stap: ${f.module}`,
        color: idx === 0 ? 'teal' : 'indigo'
      });
    });
  }
  if (recs.length >= 2) return recs.slice(0, 2);
  // 2. Weakest families → Focus Sprint
  const weakest = getWeakestFamiliesGlobal(familyMastery, selectedGroup, 5).filter(w => hasFamilyQuestionsForGroup(w.familyId, selectedGroup));
  if (weakest.length > 0 && !recs.some(r => r.familyId === weakest[0].familyId)) {
    const def = getFamilyDefinition(weakest[0].familyId);
    const score = Math.round(weakest[0].masteryScore);
    recs.push({
      id: 'sprint',
      familyId: weakest[0].familyId,
      title: 'Focus Sprint',
      reason: def ? `Focus op ${def.description} (${score}%)` : `Train je zwakste onderwerp (${score}%)`,
      color: 'indigo'
    });
  }
  if (recs.length >= 2) return recs.slice(0, 2);
  // 3. Fluency-ready families
  let hasFluencyFamilies = false;
  for (const mod of MODULES) {
    const counts = countFamiliesByMasteryLevel(familyMastery, mod.id, selectedGroup);
    if (counts.oefenaar > 0 || counts.gevorderd > 0) {
      hasFluencyFamilies = true;
      break;
    }
  }
  if (hasFluencyFamilies) {
    recs.push({
      id: 'fluency',
      title: 'Vloeiend Oefenen',
      reason: 'Je kent het al — oefen nu op snelheid zodat het automatisch gaat',
      color: 'amber'
    });
  }
  if (recs.length >= 2) return recs.slice(0, 2);
  // 4. Challenge-ready
  const hasChallengeFamilies = Object.values(familyMastery).some(m => m.masteryScore >= 70 && m.attempts > 0);
  if (hasChallengeFamilies) {
    recs.push({
      id: 'challenge',
      title: 'Uitdaging',
      reason: 'Je beheerst dit goed genoeg — probeer het nu op een hoger niveau',
      color: 'violet'
    });
  }
  // Fallback
  if (recs.length === 0) {
    recs.push({
      id: 'fluency',
      title: 'Vloeiend Oefenen',
      reason: 'Oefen dagelijks op snelheid — zo wordt het automatisch',
      color: 'amber'
    }, {
      id: 'sprint',
      title: 'Focus Sprint',
      reason: 'Kies een onderwerp en train het grondig met uitleg',
      color: 'indigo'
    });
  }
  if (recs.length === 1) {
    recs.push(recs[0].id !== 'fluency' ? {
      id: 'fluency',
      title: 'Vloeiend Oefenen',
      reason: 'Oefen dagelijks op snelheid — zo wordt het automatisch',
      color: 'amber'
    } : {
      id: 'sprint',
      title: 'Focus Sprint',
      reason: 'Kies een onderwerp en train het grondig met uitleg',
      color: 'indigo'
    });
  }
  return recs.slice(0, 2);
}
// ─── Component ────────────────────────────────────────────────
/**
 * MissionBriefing — Djay's personalized training recommendations.
 *
 * Uses the LDG frontier, weakest families, and mastery levels to suggest
 * the two most relevant practice modes for the student.
 * Part of the learningLab domain (DDD co-locatie s57).
 */
export function MissionBriefing({
  onModeClick
}: MissionBriefingProps) {
  const {
    state,
    selectedGroup
  } = useAppStore();
  const familyMastery = state.familyMastery || {};
  const recommendations = useMemo(() => buildRecommendations(familyMastery, selectedGroup), [familyMastery, selectedGroup]);
  return <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-2826">
      {recommendations.map((rec, index) => {
      const colors = COLOR_CLASSES[rec.color];
      const RecIcon = MODE_ICON[rec.id as RegistryModeId] || Target;
      const registryEntry = MODE_REGISTRY[rec.id as RegistryModeId];
      const rawEstimate = registryEntry?.estimatedMinutes;
      const estimateMin = typeof rawEstimate === 'number' ? rawEstimate : undefined;
      const masteryScore = rec.familyId ? familyMastery[getFamilyMasteryKey(rec.familyId, selectedGroup)]?.masteryScore : undefined;
      const isPrimary = index === 0;
      return <CardSurface key={`${rec.id}-${rec.familyId || index}`} onClick={() => onModeClick(rec.id, rec.familyId)} className={`rounded-2xl p-4 sm:p-5 bg-gradient-to-br ${colors.gradient} text-white shadow-md ${colors.shadow} overflow-hidden group flex flex-col h-full ${isPrimary ? 'md:col-span-2' : 'md:col-span-1'} before:absolute before:top-0 before:right-0 before:w-32 before:h-32 before:bg-white/20 before:rounded-full before:blur-2xl before:-translate-y-1/2 before:translate-x-1/4 before:pointer-events-none`} data-id="element-2827">
            <div className={`flex items-start gap-4 relative z-10 flex-1 w-full ${isPrimary ? 'sm:items-center' : ''}`} data-id="element-2828">
              <div className={`${isPrimary ? 'w-14 h-14 sm:w-16 sm:h-16' : 'w-12 h-12'} bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`} data-id="element-2829">
                <RecIcon className={`${isPrimary ? 'w-7 h-7 sm:w-8 sm:h-8' : 'w-6 h-6'} text-white`} strokeWidth={2.5} data-id="element-2830" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-2831">
                {isPrimary && <div className="text-[10px] font-black uppercase tracking-wider text-white/80 mb-1" data-id="element-2832">
                    Beste keuze voor jou
                  </div>}
                <h3 className={`${isPrimary ? 'text-xl sm:text-2xl' : 'text-lg'} font-black leading-tight mb-1`} data-id="element-2833">
                  {rec.title}
                </h3>
                <p className={`text-white/90 ${isPrimary ? 'text-sm sm:text-base' : 'text-sm'} font-medium leading-snug`} data-id="element-2834">
                  {rec.reason}
                </p>
                {/* F-UI-2: context chips — mastery + time */}
                <div className="mt-3 flex flex-wrap items-center gap-2" data-id="element-2835">
                  {masteryScore !== undefined && <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/20 backdrop-blur-sm text-[10px] font-black uppercase tracking-wider" data-id="element-2836">
                      {Math.round(masteryScore)}% beheerst
                    </span>}
                  {estimateMin !== undefined && <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/20 backdrop-blur-sm text-[10px] font-black uppercase tracking-wider" data-id="element-2837">
                      <Clock className="w-3 h-3" data-id="element-2838" />
                      {estimateMin}m
                    </span>}
                </div>
              </div>
            </div>

            <div className="mt-4 pt-3 border-t border-white/20 flex items-center justify-end relative z-10 w-full" data-id="element-2839">
              <span className="text-xs font-bold text-white/90 mr-2 uppercase tracking-wider group-hover:text-white transition-colors" data-id="element-2840">
                Start Missie
              </span>
              <ArrowRight className="w-5 h-5 text-white/80 group-hover:text-white group-hover:translate-x-1 transition-all" data-id="element-2841" />
            </div>
          </CardSurface>;
    })}
    </div>;
}